import { useState } from "react";
import { YStack, XStack, Text } from "tamagui";
import { colors } from "@styles/colors";
import { Header, StickyBtn, Fade, RADIUS } from "./Common";
import { Ic, Ar } from "./Icons";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { OTPInputBlocks } from "@components/form/OTPInputBlocks";
import api, { handleRequest } from "@services/axios/apiManager";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { appToast } from "@components/Toast";
import { rememberPreferredContext } from "@utils/line/preferredContext";
import { getReferredCourierId, getReferredOrderId, clearReferredCourierId } from "@utils/referredCourier";

interface JoinResult {
  // 邀請碼綁新瓦斯行開新身分案（docs/2026-09-03-invite-code-supplier-context-design.md）：
  // 舊後端理論上不會缺此欄位，但型別放寬為 optional 以防禦性處理、並讓
  // rememberPreferredContext 的 null 保護派上用場。
  context_id?: number;
  status?: string;
  message?: string;
}

interface ContextListItem {
  context_id: number;
  context_name: string;
  role?: "owner" | "member";
  primary_supplier?: { supplier_name?: string } | null;
}

interface JoinSerialProps {
  onBack: () => void;
  onSuccess: (info: Record<string, unknown>) => void;
  /** 瓦斯行序號連結深連結預填（見 addAccount.tsx 的 ?serial=）。只是預填，仍要使用者按確認送出。 */
  initialSerial?: string;
}

/** 取後端錯誤訊息。 */
const extractErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const data = (error as { response?: { data?: { message?: unknown } } }).response?.data;
    if (data && typeof data.message === "string") return data.message;
  }
  return "驗證序號失敗，請確認後再試";
};

export default function JoinSerial({ onBack, onSuccess, initialSerial = "" }: JoinSerialProps) {
  const { cusJwtToken } = useCusAuth();
  // 預填不觸發自動送出：OTPInputBlocks 的 onComplete 只在使用者打字／貼上時觸發，
  // 不會因為 value 這個 prop 初始值就自動 fire（見 handleChange/handlePaste 實作）。
  const [val, setVal] = useState(initialSerial);
  const [loading, setLoading] = useState(false);

  const ok = val.trim().length >= 4;

  const enrichInfo = async (result: JoinResult): Promise<Record<string, unknown>> => {
    try {
      const res = await api.get("/app-api/customer/contexts", {
        headers: { Authorization: `Bearer ${cusJwtToken}` },
      });
      // contexts API 回 { contexts, default_context_id }（見 IdentitySwitchMenu）；
      // 防禦性兼容直接回陣列的舊形狀。
      const data = res.data?.data;
      const list = (Array.isArray(data) ? data : (data?.contexts ?? [])) as ContextListItem[];
      const joined = list.find((c) => c.context_id === result.context_id);
      if (joined) {
        return {
          name: joined.context_name,
          vendor: joined.primary_supplier?.supplier_name ?? "瓦斯行",
          role: joined.role === "owner" ? "負責人" : "成員",
          status: result.status,
          context_id: result.context_id,
        };
      }
    } catch {
      // ignore
    }
    return {
      name: "我的瓦斯帳戶",
      vendor: "瓦斯行",
      role: "成員",
      status: result.status,
      context_id: result.context_id,
    };
  };

  // completeVal：OTPInputBlocks 的 onComplete 會把「含最後一碼的完整值」當參數傳入。
  // 不能讀 state 的 val——輸入第 6 碼觸發 onComplete 的當下 setVal 尚未 commit，
  // 閉包裡的 val 還是 5 碼舊值，會把少一碼的序號送給後端（實測 803009 被送成 80300 →
  // 「查無此客戶序號或已過期」，2026-09-03 dev log 抓到）。typeof 守衛是因為按鈕的
  // onClick={submit} 會把 click 事件當第一個參數傳進來，不可誤當序號。
  const submit = (completeVal?: unknown) => {
    const code = (typeof completeVal === "string" ? completeVal : val).trim();
    if (code.length < 4 || loading) return;
    handleRequest<{ data: JoinResult }>({
      asyncRequest: () =>
        api.post(
          "/app-api/customer/contexts/join-by-serial",
          {
            authentication_code: code,
            // DAPP 序號連結帶的配送員 id（?courierId=）：後端以 DynamoDB 邀請碼記錄為主、此值為備援，
            // 會驗證隸屬瓦斯行，不符只忽略。無值時不帶。
            courier_id: getReferredCourierId() ?? undefined,
            // 客戶掃的是哪一張訂單的 QR（連結 ?orderId=），後端驗證屬於該 CIS。無值時不帶。
            order_id: getReferredOrderId() ?? undefined,
          },
          { headers: { Authorization: `Bearer ${cusJwtToken}` } },
        ),
      successCallback: async (res) => {
        const data = res.data?.data;
        if (!data?.context_id) {
          appToast.error("查無此客戶序號或已過期");
          return;
        }
        // 記一次性偏好身分：身分選單下次載入自動切到這個（新開或加入的）身分。
        rememberPreferredContext(data.context_id);
        clearReferredCourierId(); // 序號用畢即清，避免殘留歸因到下一次操作
        const info = await enrichInfo(data);
        onSuccess(info);
      },
      errorCallback: (error) => {
        appToast.error(extractErrorMessage(error));
      },
      setLoading,
    });
  };

  return (
    <RadiusContentWrapper padding="0" space={0} background="transparent" contentContainerStyle={{ paddingBottom: 120 }}>
      <Header title="客戶序號配對" sub="輸入瓦斯行提供的客戶序號，加入既有帳戶" onBack={onBack} color={colors.primary} />

      <YStack p="$5" gap="$4">
        <Fade i={0}>
          <YStack bg={colors.light} br={RADIUS} p="$5" style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }} gap="$4" opacity={loading ? 0.65 : 1}>
            <Text fontSize={12} color={colors.gray} fontWeight="700" ta="center">
              客戶序號
            </Text>
            <OTPInputBlocks value={val} onChange={setVal} digits={6} onComplete={submit} />
            <Text fontSize={11} color={colors.gray200} ta="center" mt="$2">
              💡 序號可向您合作的瓦斯行索取
            </Text>
          </YStack>
        </Fade>
        <Fade i={1}>
          <YStack bg={colors.primaryActive} br={RADIUS} p="$4">
            <XStack ai="center" gap="$2" mb="$2">
              <Ic d="M12 9v4M12 17h.01M21 12A9 9 0 113 12a9 9 0 0118 0z" c={colors.primary} s={15} />
              <Text fontSize={12} fontWeight="700" color={colors.primary}>
                為什麼要輸入序號？
              </Text>
            </XStack>
            <Text fontSize={11} color={colors.primary} opacity={0.8} lineHeight={18}>
              輸入序號後，系統會把您加入該瓦斯行帳戶，並帶入歷史資料、剩餘瓦斯量與優惠權益。
            </Text>
          </YStack>
        </Fade>
        <StickyBtn
          label={loading ? "驗證中…" : "驗證序號並加入"}
          disabled={!ok || loading}
          onClick={submit}
          color={colors.primary}
          icon={ok && !loading ? <Ar c="#fff" s={14} /> : null}
        />
      </YStack>
    </RadiusContentWrapper>
  );
}
